import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  ButtonGroup,
  BackButton,
  SubmitButton,
  StyledForm,
} from "../../ManagementTools/component/styles";
import { Formik } from "formik";
import { CustomFileUpload } from "../../../common/FormInputs";
import {
  Modal,
  makeStyles,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@material-ui/core";
import { getErrorMessage, getFileName } from "../../../../helpers/functions";
import useLoader from "../../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import AWSService from "../../../../services/AWSService";
import NoticeService from "../../../../services/NoticeService";
import { reGenerateValidationSchema } from "../../../../helpers/validationSchema";
import ValidationErrors from "../../NoticeGeneration/component/ValidationError";
import ResponseModal from "../../../pages/AssignNeutralBulkCase/ResponseModal";
import FormField from "../../../common/FormField/FormField";
import theme from "../../../../assets/theme";
import COLORS from "../../../../assets/Colors";

const useStyles = makeStyles((themes) => ({
  formLabel: {
    fontFamily: "openSans-SemiBold, sans-serif",
    color: "#293461",
    fontSize: 14,
  },
}));

const excel_upload = {
  name: "excelFile",
  label: "Upload Xlsx",
  accept: ".xlsx",
};

const loan_inputs = [
  {
    type: "select",
    name: "loanType",
    label: "Loan Type",
    required: false,
    items: [
      { label: "Vehicle Loan", value: "vehicleLoan" },
      { label: "Business Loan", value: "businessLoan" },
      { label: "Personal Loan", value: "personalLoan" },
    ],
  },
  {
    type: "select",
    name: "loanStatus",
    label: "Loan Status",
    required: false,
    items: [
      { label: "Matured", value: "matured" },
      { label: "Short Fall", value: "shortFall" },
      { label: "Running", value: "running" },
    ],
  },
];

const letter_head_types = [
  { label: "Image", name: "image", value: "image" },
  { label: "Text", name: "text", value: "text" },
];

const input_alignments = [
  { label: "Left", value: "left" },
  { label: "Center", value: "center" },
  { label: "Right", value: "right" },
];

const GenerateNotice = ({ paperModal, setPaperModal }) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const [modal, setModal] = useState({ state: false });
  const [showResponseModal, setShowResponseModal] = useState({
    state: false,
  });
  const [bulkReviewModal, setBulkReviewModal] = useState({ state: false });
  const classes = useStyles();
  const [letterHeadOption, setLetterHeadOption] = useState({});
  const [letterHeadNames, setLetterHeadNames] = useState([]);

  const metaData = paperModal?.metaData;
  const isOverlayOpen =
    modal?.state || showResponseModal?.state || bulkReviewModal?.state;

  async function s3Upload(file, field, setFieldValue, setFieldError) {
    if (file) {
      try {
        setLoader({ state: true, message: `Uploading file ${file.name} ...` });
        const payload = {
          key: file.name,
        };
        const s3Response = await AWSService.getS3URL(payload);
        if (s3Response.url) {
          const res = await AWSService.uploadToS3(s3Response.url, file);
          setFieldValue(field, res);
        }
      } catch (error) {
        const message = getErrorMessage(error);
        setFieldError(field, message);
      } finally {
        setLoader({ state: false });
      }
    }
  }

  useEffect(() => {
    async function getNoticeTemplates(partyId) {
      try {
        setLoader({ state: true, message: "fetching notice details..." });
        const response = await NoticeService.getNoticeTemplates(partyId);
        if (response[0]) {
          setLetterHeadNames(response[0]?.letterHeadArr || []);
          setLetterHeadOption(response[0]?.letterHeadOption || {});
        }
      } catch (err) {
        enqueueSnackbar(err?.message, {
          variant: "error",
        });
      } finally {
        setLoader({ state: false });
      }
    }

    if (paperModal?.metaData?.partyId) {
      getNoticeTemplates(paperModal?.metaData?.partyId);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paperModal?.metaData?.partyId]);

  async function onFormSubmit(values) {
    try {
      setLoader({ state: true, message: "Regenerate notice..." });
      const source =
        values?.letterHeadName &&
        letterHeadOption[values?.letterHeadName]?.length &&
        letterHeadOption[values?.letterHeadName][0];
      let letterHeadPayload;
      if (source) {
        if (source.letterHeadType === "image") {
          letterHeadPayload = {
            id: source.id,
            headerImage: source.headerImage || "",
            footerImage: source.footerImage || "",
            letterHeadType: "image",
          };
        } else if (source.letterHeadType === "text") {
          letterHeadPayload = {
            id: source.id,
            letterHeadType: "text",
            headerFooterText: {
              headerText: source.headerFooterText?.headerText || [],
              footerText: source.headerFooterText?.footerText || [],
            },
            headerAlignment: values?.headerAlignment || "left",
            footerAlignment: values?.footerAlignment || "left",
          };
        }
      } else {
        letterHeadPayload = { headerImage: "", footerImage: "" };
      }
      let payload = {
        ...paperModal?.metaData,
        excelFile: values?.excelFile,
        loanType: values?.loanType,
        loanStatus: values?.loanStatus,
        letterHeadName: values?.letterHeadName,
        letterHeadImages: letterHeadPayload,
      };

      const response = await NoticeService.bulkNoticeGenerate(payload);

      const bulkReview = response?.reports
        ? response
        : response?.data?.reports
          ? response.data
          : null;

      if (bulkReview?.reports) {
        setBulkReviewModal({ state: true, data: bulkReview, payload });
        return;
      }

      const datatypeError = response?.error || response?.data?.error;

      if (datatypeError?.previewExcel || datatypeError?.datatypeErrors) {
        enqueueSnackbar(response?.message, {
          variant: "error",
        });
        setShowResponseModal({
          state: true,
          datatypeErrors: datatypeError.datatypeErrors,
          errorExcel: datatypeError.errorExcel,
          previewExcel: datatypeError.previewExcel,
        });
        return;
      }

      if (response?.error?.missingKeys?.length > 0) {
        setModal({
          state: true,
          metaData: response?.error,
        });
        return;
      }

      if (response?.message) {
        enqueueSnackbar(response?.message, {
          variant: "success",
        });
      }
      setPaperModal({ state: false });
    } catch (err) {
      const error = getErrorMessage(err);
      enqueueSnackbar(error, {
        variant: "error",
      });
      const datatypeError =
        err?.error || err?.data?.error || err?.response?.data?.error;
      if (datatypeError?.previewExcel || datatypeError?.datatypeErrors) {
        setShowResponseModal({
          state: true,
          datatypeErrors: datatypeError.datatypeErrors,
          errorExcel: datatypeError.errorExcel,
          previewExcel: datatypeError.previewExcel,
        });
      }
    } finally {
      setLoader({ state: false });
    }
  }

  async function onReviewConfirm({ datatypeFile, headerOverrides }) {
    try {
      setLoader({ state: true, message: "Generating notice..." });
      const s3Response = await AWSService.getS3URL({
        key: datatypeFile.name,
      });
      const datatypeFileUrl = await AWSService.uploadToS3(
        s3Response.url,
        datatypeFile,
      );
      const response = await NoticeService.bulkNoticeGenerate({
        ...bulkReviewModal?.payload,
        datatypeFile: datatypeFileUrl,
        ...(Object.keys(headerOverrides || {}).length && {
          headerOverrides,
        }),
        confirmGenerate: true,
      });
      const previewResponse =
        response?.error || response?.data?.error || response;
      if (previewResponse?.previewExcel) {
        enqueueSnackbar(response?.message || "Notice Generation failed", {
          variant: "error",
        });
        setBulkReviewModal({ state: false });
        setShowResponseModal({
          state: true,
          datatypeErrors: previewResponse.datatypeErrors || [],
          errorExcel: previewResponse.errorExcel,
          previewExcel: previewResponse.previewExcel,
        });
        return;
      }
      if (response?.message) {
        enqueueSnackbar(response.message, { variant: "success" });
      }
      setBulkReviewModal({ state: false });
      setPaperModal({ state: false });
    } catch (error) {
      const previewResponse =
        error?.error || error?.data?.error || error?.response?.data;
      if (previewResponse?.previewExcel) {
        enqueueSnackbar(
          previewResponse?.message || "Notice Generation failed",
          {
            variant: "error",
          },
        );
        setBulkReviewModal({ state: false });
        setShowResponseModal({
          state: true,
          datatypeErrors: previewResponse.datatypeErrors || [],
          errorExcel: previewResponse.errorExcel,
          previewExcel: previewResponse.previewExcel,
        });
      } else {
        enqueueSnackbar(getErrorMessage(error), { variant: "error" });
      }
    } finally {
      setLoader({ state: false });
    }
  }

  return (
    <React.Fragment>
      {/* keepMounted preserves the form values while an error/review modal is on top */}
      <Modal
        open={Boolean(paperModal?.state) && !isOverlayOpen}
        onClose={() => setPaperModal({ state: false })}
        disableRestoreFocus
        keepMounted
      >
        <DrawerContainer>
          <ModalHeader>
            <HeaderText>
              <ModalTitle>Regenerate Notice</ModalTitle>
              <ModalSubtitle>
                Upload the corrected excel to regenerate the notice
              </ModalSubtitle>
            </HeaderText>
            <CloseModal
              onClick={() => setPaperModal({ state: false })}
              src={require("../../../../assets/images/closeModal.svg")}
            />
          </ModalHeader>

          {(metaData?.noticeCategory || metaData?.templateCode) && (
            <InfoRow>
              {metaData?.noticeCategory && (
                <InfoChip>
                  <InfoLabel>Notice Category</InfoLabel>
                  <InfoValue>{metaData.noticeCategory}</InfoValue>
                </InfoChip>
              )}
              {metaData?.templateCode && (
                <InfoChip>
                  <InfoLabel>Template Code</InfoLabel>
                  <InfoValue>{metaData.templateCode}</InfoValue>
                </InfoChip>
              )}
            </InfoRow>
          )}

          <ModalBody>
            <Formik
              initialValues={{
                excelFile: "",
                fileUpload: "",
                loanType: "",
                loanStatus: "",
                letterHeadName: "",
                headerAlignment: "left",
                footerAlignment: "left",
                typeOfLetterHead: "image",
              }}
              validationSchema={reGenerateValidationSchema}
              enableReinitialize
              onSubmit={onFormSubmit}
              validateOnBlur
              validateOnChange
            >
              {({
                values,
                handleChange,
                errors,
                touched,
                handleBlur,
                handleSubmit,
                setFieldValue,
                setFieldError,
              }) => {
                const menuHeaderFooter = [
                  { label: "None", value: "" },
                  ...letterHeadNames
                    .filter(
                      (name) =>
                        letterHeadOption[name]?.length &&
                        letterHeadOption[name][0]?.letterHeadType ===
                          values.typeOfLetterHead,
                    )
                    .map((name) => ({
                      label: name,
                      value: name,
                    })),
                ];

                const renderFields = (fields) =>
                  fields.map((input) => (
                    <FormField
                      key={input.name}
                      menuItems={input.items}
                      {...{
                        touched,
                        errors,
                        input,
                        values,
                        handleChange,
                        handleBlur,
                        setFieldValue,
                      }}
                    />
                  ));

                return (
                  <StyledForm>
                    <SectionLabel>
                      {excel_upload.label}
                      <span className="required-star"> *</span>
                    </SectionLabel>
                    <CustomFileUpload
                      error={
                        touched[excel_upload.name] && errors[excel_upload.name]
                      }
                      setFile={(file) =>
                        s3Upload(
                          file,
                          excel_upload.name,
                          setFieldValue,
                          setFieldError,
                        )
                      }
                      style={{ width: "100%" }}
                      accept={excel_upload.accept}
                      file={{
                        name: getFileName(values[excel_upload.name]),
                      }}
                    />

                    <Divider />

                    <SectionLabel>Loan Details</SectionLabel>
                    <FieldGrid>{renderFields(loan_inputs)}</FieldGrid>

                    <Divider />

                    <SectionLabel>Letter Head Type</SectionLabel>
                    <RadioGroup
                      style={{ flexDirection: "row", gap: "8px" }}
                      name="typeOfLetterHead"
                      value={values.typeOfLetterHead}
                      onChange={(e) => {
                        setFieldValue("typeOfLetterHead", e.target.value);
                        setFieldValue("letterHeadName", "");
                      }}
                    >
                      {letter_head_types.map((radio) => (
                        <RadioChip
                          key={radio.value}
                          selected={values.typeOfLetterHead === radio.value}
                        >
                          <FormControlLabel
                            classes={{ label: classes.formLabel }}
                            value={radio.value}
                            control={
                              <Radio
                                size="small"
                                style={{
                                  color: COLORS.BTN_GREEN,
                                  padding: "6px",
                                }}
                              />
                            }
                            label={radio.label}
                            style={{ margin: 0 }}
                          />
                        </RadioChip>
                      ))}
                    </RadioGroup>

                    <FieldGrid style={{ marginTop: 8 }}>
                      {renderFields([
                        {
                          type: "select",
                          name: "letterHeadName",
                          label: "Letter Head Name",
                          required: false,
                          items: menuHeaderFooter,
                        },
                      ])}
                    </FieldGrid>

                    {values?.typeOfLetterHead === "text" && (
                      <FieldGrid>
                        {renderFields([
                          {
                            type: "select",
                            name: "headerAlignment",
                            label: "Header Alignment",
                            required: false,
                            items: input_alignments,
                          },
                          {
                            type: "select",
                            name: "footerAlignment",
                            label: "Footer Alignment",
                            required: false,
                            items: input_alignments,
                          },
                        ])}
                      </FieldGrid>
                    )}

                    <ButtonGroup>
                      <BackButton
                        onClick={() => setPaperModal({ state: false })}
                      >
                        Close
                      </BackButton>
                      <SubmitButton onClick={handleSubmit}>
                        Regenerate
                      </SubmitButton>
                    </ButtonGroup>
                  </StyledForm>
                );
              }}
            </Formik>
          </ModalBody>
        </DrawerContainer>
      </Modal>

      {modal?.state && <ValidationErrors {...{ modal, setModal }} />}
      <ResponseModal
        open={showResponseModal?.state}
        onClose={() => setShowResponseModal({ state: false })}
        datatypeErrors={showResponseModal?.datatypeErrors}
        errorExcel={showResponseModal?.errorExcel}
        previewExcel={showResponseModal?.previewExcel}
        title="Datatype Validation"
      />
      <ResponseModal
        open={bulkReviewModal?.state}
        onClose={() => setBulkReviewModal({ state: false })}
        bulkReview={bulkReviewModal?.data}
        onReviewConfirm={onReviewConfirm}
        title="Excel Review"
      />
    </React.Fragment>
  );
};

export default GenerateNotice;

const DrawerContainer = styled.div`
  max-width: 560px;
  width: calc(100% - 32px);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  background-color: white;
  outline: none;
  position: absolute;
  top: 50%;
  left: 50%;
  border-radius: 12px;
  transform: translate(-50%, -50%);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  border: 1px solid #a5bfcc;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 24px 28px 16px;
  border-bottom: 1.5px solid rgba(0, 131, 140, 0.1);
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ModalTitle = styled.div`
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 18px;
  color: ${COLORS.COLOR_DARK};
`;

const ModalSubtitle = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 13px;
  color: ${COLORS.INPUT_LABEL};
`;

const CloseModal = styled.img`
  width: 14px;
  height: 14px;
  margin-top: 6px;
  object-fit: contain;
  cursor: pointer;
`;

const InfoRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 14px 28px 0;
`;

const InfoChip = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(0, 131, 140, 0.06);
  min-width: 0;
`;

const InfoLabel = styled.span`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${COLORS.INPUT_LABEL};
`;

const InfoValue = styled.span`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  color: ${COLORS.COLOR_DARK};
  word-break: break-word;
`;

const ModalBody = styled.div`
  padding: 16px 28px 24px;
  overflow-y: auto;
`;

const SectionLabel = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  font-weight: 700;
  color: ${COLORS.INPUT_LABEL};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 16px;
  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Divider = styled.div`
  height: 1px;
  background: linear-gradient(90deg, rgba(0, 131, 140, 0.12), transparent);
  margin: 18px 0;
`;

const RadioChip = styled.div`
  padding: 2px 12px 2px 4px;
  border-radius: 12px;
  border: 1.5px solid
    ${({ selected }) => (selected ? COLORS.BTN_GREEN : "#e1e3ee")};
  background: ${({ selected }) =>
    selected ? "rgba(0, 131, 140, 0.07)" : "#f9fbfc"};
  transition: all 0.2s ease;
  cursor: pointer;
`;
